import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { type Columns, DataTable } from '../components/ui/data-table';
import { Input, Select } from '../components/ui/input';
import { API_BASE, api, errorMessage } from '../lib/api';
import type { InvoiceRow } from '../lib/types';
import { formatDateTime, formatMoney } from '../lib/utils';

/** Инвойсы и кредит-ноты: выпускаются автоматически при оплате и возврате денег. */
export function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const [type, setType] = useState<'' | 'invoice' | 'credit_note'>('');
  const [q, setQ] = useState('');
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (q.trim()) params.set('q', q.trim());
  const list = useQuery({
    queryKey: ['invoices', type, q.trim()],
    queryFn: () => api<{ items: InvoiceRow[] }>(`/invoices?${params.toString()}`),
  });

  const columns = useMemo<Columns<InvoiceRow>>(
    () => [
      {
        id: 'number',
        header: t('inv.number'),
        accessorFn: (r) => r.fullNumber,
        cell: ({ row }) => (
          <a
            href={`${API_BASE}/invoices/${row.original.id}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            <FileText className="size-4" />
            {row.original.fullNumber}
          </a>
        ),
      },
      {
        id: 'type',
        header: t('inv.type'),
        accessorFn: (r) => r.type,
        cell: ({ row }) => (
          <Badge variant={row.original.type === 'credit_note' ? 'warning' : 'secondary'}>
            {t(`inv.types.${row.original.type}`)}
          </Badge>
        ),
      },
      { id: 'order', header: t('po.order'), accessorFn: (r) => r.orderNumber ?? '—' },
      {
        id: 'buyer',
        header: t('inv.buyer'),
        accessorFn: (r) => r.buyer,
        cell: ({ row }) => (
          <span>
            {row.original.buyer}
            {row.original.buyerVatId ? (
              <a
                href={`${API_BASE}/invoices/${row.original.id}/ubl`}
                className="ml-2 text-xs text-primary hover:underline"
              >
                e-invoice
              </a>
            ) : null}
          </span>
        ),
      },
      {
        id: 'taxMode',
        header: t('inv.taxMode'),
        accessorFn: (r) => t(`inv.taxModes.${r.taxMode}`),
      },
      {
        id: 'total',
        header: t('po.total'),
        accessorFn: (r) => r.totalMinor,
        cell: ({ row }) =>
          formatMoney(row.original.totalMinor, row.original.currency, i18n.language),
      },
      {
        id: 'issued',
        header: t('inv.issuedAt'),
        accessorFn: (r) => r.issuedAt,
        cell: ({ row }) => formatDateTime(row.original.issuedAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <PageHeader title={t('inv.title')} description={t('inv.hint')} />
      <div className="mb-4 flex flex-wrap gap-2">
        <Select
          aria-label={t('inv.type')}
          value={type}
          onChange={(e) => {
            setType(e.target.value as '' | 'invoice' | 'credit_note');
          }}
          className="w-48"
        >
          <option value="">{t('inv.allTypes')}</option>
          <option value="invoice">{t('inv.types.invoice')}</option>
          <option value="credit_note">{t('inv.types.credit_note')}</option>
        </Select>
        <Input
          aria-label={t('inv.search')}
          placeholder={t('inv.search')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
          className="w-64"
        />
      </div>
      {list.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(list.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isPending}
        rowKey={(r) => r.id}
      />
    </>
  );
}
